import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Chip,
  FormControlLabel,
  FormGroup,
  Stack,
  Switch,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { de, enGB, es, fr } from 'date-fns/locale';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import i18n from '../../../i18n';
import { FilterResponse } from '../../../services/types/filters';
import { CustomButton } from '../../common/button/customButton';
import fontStyle from '../../common/button/fontStyle';
import { useFilterContext } from '../../context/filterContext';
import { FilterDialog, FilterOption } from './filterDialog/filterDialog';

enum ContractStatus {
  active = 'active',
  inactive = 'inactive',
  none = 'none',
  empty = '',
}

// from https://upmostly.com/typescript/implementing-groupby-in-typescript
function groupBy<T>(arr: T[], fn: (item: T) => any) {
  return arr.reduce<Record<string, T[]>>((prev, curr) => {
    const groupKey = fn(curr);
    const group = prev[groupKey] || [];
    group.push(curr);
    return { ...prev, [groupKey]: group };
  }, {});
}
const groupFilterOptionValuesByLabel = (
  filterOptions: FilterOption[],
): FilterOption[] => {
  const groupedByLabel = groupBy(
    filterOptions,
    (filterOption) => filterOption.label,
  );

  const grouped = Object.keys(groupedByLabel).map((label) => {
    const values = groupedByLabel[label].map(
      (filterOption) => filterOption.value,
    );
    return {
      label,
      value: JSON.stringify(values),
    };
  });

  return grouped;
};

const deleteDuplictesFromArray = (array: any[]) => {
  return array.filter((item: any, index) => {
    return array.indexOf(item) === index;
  });
};

export const Filters = ({
  filters,
  handleOnFilterPress,
  setResetPressed,
}: {
  filters: FilterResponse | null;
  handleOnFilterPress: (FilterUpToRows?: number, FilterOffset?: number) => void;
  setResetPressed: (resetPressed: boolean) => void;
}) => {
  const { t } = useTranslation();

  const currentFilterContext = useFilterContext();

  const [divisionOptions, setDivisionOptions] = useState<FilterOption[]>([]);
  const [countryOptions, setCountryOptions] = useState<FilterOption[]>([]);
  const [shipTopPartyOptions, setShipTopPartyOptions] = useState<
    FilterOption[]
  >([]);
  const [contractTypeOptions, setContractTypeOptions] = useState<
    FilterOption[]
  >([]);
  const [contractUsageOptions, setContractUsageOptions] = useState<
    FilterOption[]
  >([]);

  const [divisionDialogOpen, setDivisionDialogOpen] = useState(false);
  const [countryDialogOpen, setCountryDialogOpen] = useState(false);
  const [contractTypeDialogOpen, setcontractTypeDialogOpen] = useState(false);
  const [shipTopPartyDialogOpen, setShipToPartyDialogOpen] = useState(false);
  const [contractUsageDialogOpen, setContractUsageDialogOpen] = useState(false);
  const [divisonCount, setDivisionCount] = useState(
    currentFilterContext.divisions.length,
  );
  const [shipToPartyCount, setShipToPartyCount] = useState(
    currentFilterContext.shipToParties.length,
  );
  const [countryCount, setCountryCount] = useState(
    currentFilterContext.countries.length,
  );

  const [beginDateStartError, setBeginDateStartError] = useState(false);
  const [beginDateEndError, setBeginDateEndError] = useState(false);

  const beginDateStartInitialSelection = null;
  const beginDateEndInitialSelection = null;
  const divisionInitialSelection = divisionOptions.flatMap((e) => e.value);
  const countriesInitialSelection =
    groupFilterOptionValuesByLabel(countryOptions);
  const shipToPartiesInitialSelection = shipTopPartyOptions.flatMap(
    (e) => e.value,
  );

  useEffect(() => {
    if (
      currentFilterContext.activeContracts === true &&
      currentFilterContext.finishedContracts === false
    ) {
      currentFilterContext.setContractStatus(ContractStatus.active);
    } else if (
      currentFilterContext.activeContracts === false &&
      currentFilterContext.finishedContracts === true
    ) {
      currentFilterContext.setContractStatus(ContractStatus.inactive);
    } else if (
      currentFilterContext.activeContracts === false &&
      currentFilterContext.finishedContracts === false
    ) {
      currentFilterContext.setContractStatus(ContractStatus.none);
    } else {
      currentFilterContext.setContractStatus(ContractStatus.empty);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentFilterContext.activeContracts,
    currentFilterContext.finishedContracts,
  ]);

  useEffect(() => {
    if (filters) {
      setDivisionOptions(
        filters.locationRights.map((locationRight) => {
          return {
            value: locationRight.divisionNr.toString(),
            label: locationRight.label,
          };
        }),
      );

      setCountryOptions(
        filters.locationRights.flatMap((locRight) => {
          const countries = locRight.countries
            .map((countr) => {
              return {
                label: t(countr.countryName),
                value: countr.countryNr.toString(),
              };
            })
            .filter(
              (value, index, self) =>
                index === self.findIndex((stp) => stp.value === value.value),
            );
          const groupedCountries = groupFilterOptionValuesByLabel(countries);
          return groupedCountries;
        }),
      );

      setShipTopPartyOptions(
        filters.locationRights
          .flatMap((loc) =>
            loc.countries.flatMap((cou) =>
              cou.locations.flatMap((lo) =>
                lo.shipToParty.flatMap((stpp) => {
                  return { value: stpp.id.toString(), label: stpp.label };
                }),
              ),
            ),
          )
          .filter(
            (value, index, self) =>
              index === self.findIndex((stp) => stp.value === value.value),
          ),
      );

      setContractTypeOptions([
        { label: t('STR'), value: 'str' },
        { label: t('RFM'), value: 'rfm' },
      ]);

      setContractUsageOptions([
        { label: t('STR'), value: 'str' },
        { label: t('pbth'), value: 'pbth' },
        { label: t('subscription'), value: 'subscription' },
        { label: t('coreFleet'), value: 'coreFleet' },
        { label: t('plannedPeak'), value: 'plannedPeak' },
        { label: t('adhocPeak'), value: 'adhocPeak' },
        { label: t('nonStandard'), value: 'nonStandard' },
      ]);
    }
  }, [
    filters,
    currentFilterContext.shipToParties,
    currentFilterContext.divisions,
    currentFilterContext.countries,
    t,
  ]);

  useEffect(() => {
    const tmpShipToParties: string[] = [];
    const tmpCountries: FilterOption[] = [];
    const tmpDivisions: string[] = [];
    filters?.locationRights.forEach((locationRight) => {
      tmpDivisions.push(locationRight.divisionNr.toString());
      locationRight.countries.forEach((countryTmp) => {
        tmpCountries.push({
          label: t(countryTmp.countryName),
          value: countryTmp.countryNr.toString(),
        });
        countryTmp.locations.forEach((location) => {
          location.shipToParty.forEach((shipToPartyTmpTmp) => {
            if (shipToPartyTmpTmp) {
              tmpShipToParties.push(shipToPartyTmpTmp.id.toString());
            }
          });
        });
      });
    });
    if (currentFilterContext.shipToParties.length === 0) {
      currentFilterContext.setShipToParties(
        deleteDuplictesFromArray(tmpShipToParties),
      );
    }
    if (currentFilterContext.countries.length === 0) {
      currentFilterContext.setCountries(
        groupFilterOptionValuesByLabel(tmpCountries),
      );
    }
    if (currentFilterContext.divisions.length === 0) {
      currentFilterContext.setDivisions(deleteDuplictesFromArray(tmpDivisions));
    }

    setShipToPartyCount(deleteDuplictesFromArray(tmpShipToParties).length);
    setCountryCount(groupFilterOptionValuesByLabel(tmpCountries).length);
    setDivisionCount(deleteDuplictesFromArray(tmpDivisions).length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleDivisionChange = (divisionsTmp: string[]) => {
    currentFilterContext.setDivisions(divisionsTmp);
  };

  const handleCountryChange = (countriesTmp: FilterOption[]) => {
    currentFilterContext.setCountries(countriesTmp);
  };

  const handleCustomerReferenceChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    currentFilterContext.setCustomerReference(event.target.value);
  };

  const handleSerialNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    currentFilterContext.setSerialNumber(event.target.value);
  };

  const handleContractNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    currentFilterContext.setContractNumber(event.target.value);
  };

  const handleShipToPartyChange = (shipToPartiesTmp: string[]) => {
    currentFilterContext.setShipToParties(shipToPartiesTmp);
  };

  const handleContractTypeChange = (contractTypeTmp: string) => {
    currentFilterContext.setContractType(contractTypeTmp);
  };

  const handleContractUsageChange = (contractUsageTmp: string) => {
    currentFilterContext.setContractUsage(contractUsageTmp);
  };

  const handleBeginDateStartChange = (date: any) => {
    currentFilterContext.setBeginDateStart(new Date(date));
  };
  const handleBeginDateEndChange = (date: any) => {
    currentFilterContext.setBeginDateEnd(new Date(date));
  };

  const handleOnFilterResetPress = () => {
    currentFilterContext.setDivisions(divisionInitialSelection);
    currentFilterContext.setContractStatus(ContractStatus.active);
    currentFilterContext.setCountries(countriesInitialSelection);
    currentFilterContext.setShipToParties(shipToPartiesInitialSelection);
    currentFilterContext.setContractType('');
    currentFilterContext.setContractUsage('');
    currentFilterContext.setBeginDateStart(beginDateStartInitialSelection);
    currentFilterContext.setBeginDateEnd(beginDateEndInitialSelection);
    currentFilterContext.setSerialNumber('');
    currentFilterContext.setContractNumber('');
    currentFilterContext.setCustomerReference('');
    currentFilterContext.setActiveContracts(true);
    currentFilterContext.setFinishedContracts(false);
    currentFilterContext.setOffset(0);
    setResetPressed(true);
  };

  if (!filters) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <CircularProgress color="secondary" />
      </div>
    );
  }

  const setAdapterLocale = (): Locale => {
    switch (i18n.language.substring(0, 2)) {
      case 'de':
        return de;
      case 'fr':
        return fr;
      case 'es':
        return es;
      default:
        return enGB;
    }
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" sx={{ width: '100%' }}>
        <Stack direction="row" sx={{ width: '100%' }} alignItems="strech">
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={currentFilterContext.activeContracts}
                  onChange={() => {
                    currentFilterContext.setActiveContracts(
                      !currentFilterContext.activeContracts,
                    );
                  }}
                />
              }
              label={t('ActiveContracts')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={currentFilterContext.finishedContracts}
                  onChange={() => {
                    currentFilterContext.setFinishedContracts(
                      !currentFilterContext.finishedContracts,
                    );
                  }}
                />
              }
              label={t('TerminatedContracts')}
            />
          </FormGroup>
        </Stack>
      </Stack>
      <Chip
        id="divisonChip"
        sx={{ borderRadius: 0 }}
        label={`${t('Division')} (${currentFilterContext.divisions.length} ${t(
          'Of',
        )} ${divisonCount} ${t('Selected')})`}
        onClick={() => setDivisionDialogOpen(true)}
        color={
          currentFilterContext.divisions.length !== divisonCount
            ? 'primary'
            : 'default'
        }
      />
      <FilterDialog
        title={t('Division')}
        open={divisionDialogOpen}
        onClose={() => setDivisionDialogOpen(false)}
        onApply={(selectedOptions) => {
          handleDivisionChange(selectedOptions);
          setDivisionDialogOpen(false);
        }}
        selectedOptions={currentFilterContext.divisions}
        options={divisionOptions}
      />
      <Chip
        id="countryChip"
        sx={{ borderRadius: 0 }}
        label={`${t('Country')} (${currentFilterContext.countries.length} ${t(
          'Of',
        )} ${countryCount} ${t('Selected')})`}
        onClick={() => setCountryDialogOpen(true)}
        color={
          currentFilterContext.countries.length !== countryCount
            ? 'primary'
            : 'default'
        }
      />
      <FilterDialog
        title={t('Country')}
        open={countryDialogOpen}
        onClose={() => setCountryDialogOpen(false)}
        onApply={(selectedOptions) => {
          handleCountryChange(selectedOptions.map((s) => JSON.parse(s)));
          setCountryDialogOpen(false);
        }}
        selectedOptions={currentFilterContext.countries.map((c) =>
          JSON.stringify(c),
        )}
        options={countryOptions.map((c) => {
          return {
            label: c.label,
            value: JSON.stringify(c),
          };
        })}
      />
      <Chip
        id="shipTopPartyChip"
        sx={{ borderRadius: 0 }}
        label={`${t('ShipToParty')} (${
          currentFilterContext.shipToParties.length
        } ${t('Of')} ${shipToPartyCount} ${t('Selected')})`}
        onClick={() => setShipToPartyDialogOpen(true)}
        color={
          currentFilterContext.shipToParties.length !== shipToPartyCount
            ? 'primary'
            : 'default'
        }
      />
      <FilterDialog
        title={t('ShipToParty')}
        open={shipTopPartyDialogOpen}
        onClose={() => {
          setShipToPartyDialogOpen(false);
        }}
        onApply={(selectedOptions) => {
          handleShipToPartyChange(selectedOptions);
          setShipToPartyDialogOpen(false);
        }}
        selectedOptions={currentFilterContext.shipToParties}
        options={shipTopPartyOptions}
      />
      <Chip
        id="contractTypeChip"
        sx={{ borderRadius: 0 }}
        label={
          currentFilterContext.contractType === ''
            ? t('ContractType')
            : t(currentFilterContext.contractType)
        }
        onClick={() => setcontractTypeDialogOpen(true)}
        color={currentFilterContext.contractType === '' ? 'default' : 'primary'}
      />
      <FilterDialog
        title={t('ContractType')}
        radio
        open={contractTypeDialogOpen}
        onClose={() => {
          setcontractTypeDialogOpen(false);
        }}
        onApply={(selectedOptions) => {
          handleContractTypeChange(selectedOptions[0]);
          setcontractTypeDialogOpen(false);
        }}
        selectedOptions={[currentFilterContext.contractType]}
        options={contractTypeOptions}
      />
      <Chip
        id="contractUsageChip"
        sx={{ borderRadius: 0 }}
        label={
          currentFilterContext.contractUsage === ''
            ? t('Usage')
            : t(currentFilterContext.contractUsage)
        }
        onClick={() => setContractUsageDialogOpen(true)}
        color={
          currentFilterContext.contractUsage === '' ? 'default' : 'primary'
        }
      />
      <FilterDialog
        title={t('Usage')}
        radio
        open={contractUsageDialogOpen}
        onClose={() => {
          setContractUsageDialogOpen(false);
        }}
        onApply={(selectedOptions) => {
          handleContractUsageChange(selectedOptions[0]);
          setContractUsageDialogOpen(false);
        }}
        selectedOptions={[currentFilterContext.contractUsage]}
        options={contractUsageOptions}
      />
      <LocalizationProvider
        adapterLocale={setAdapterLocale()}
        dateAdapter={AdapterDateFns}
      >
        <Stack spacing={2}>
          <Stack direction="column">
            <TextField
              className="TextField-without-border-radius"
              label={t('SerialNumber')}
              variant="outlined"
              size="small"
              value={currentFilterContext.serialNumber}
              onChange={handleSerialNumberChange}
              inputProps={{
                maxLength: 18,
                style: {
                  fontSize: fontStyle.fontSize.md,
                },
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Stack>

          <Stack direction="column">
            <TextField
              className="TextField-without-border-radius"
              label={t('ContractNumber')}
              variant="outlined"
              size="small"
              value={currentFilterContext.contractNumber}
              onChange={handleContractNumberChange}
              inputProps={{
                maxLength: 10,
                style: {
                  fontSize: fontStyle.fontSize.md,
                },
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Stack>
          <Stack direction="column">
            <TextField
              className="TextField-without-border-radius"
              label={t('Reference')}
              size="small"
              onChange={handleCustomerReferenceChange}
              value={currentFilterContext.customerReference}
              inputProps={{
                maxLength: 20,
                style: {
                  fontSize: fontStyle.fontSize.md,
                },
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Stack>

          <Box>
            <Stack
              direction="column"
              justifyContent="space-between"
              width="100%"
              spacing={2}
            >
              <DatePicker
                label={t('ContractBeginStart')}
                slotProps={{
                  textField: {
                    className: 'TextField-without-border-radius',
                    size: 'small',
                    InputLabelProps: { shrink: true },
                  },
                }}
                value={currentFilterContext.beginDateStart}
                onError={(value) => {
                  setBeginDateStartError(value !== null);
                }}
                onChange={(value) => {
                  handleBeginDateStartChange(value);
                }}
                format={t('DateFormatShort') || ''}
              />

              <DatePicker
                label={t('ContractBeginEnd')}
                slotProps={{
                  textField: {
                    className: 'TextField-without-border-radius',
                    size: 'small',
                    InputLabelProps: { shrink: true },
                  },
                }}
                value={currentFilterContext.beginDateEnd}
                onError={(value) => {
                  setBeginDateEndError(value !== null);
                }}
                onChange={(value) => {
                  handleBeginDateEndChange(value);
                }}
                format={t('DateFormatShort') || ''}
              />
            </Stack>
          </Box>
        </Stack>
      </LocalizationProvider>

      <Stack spacing={1}>
        <CustomButton
          sx={{
            fontFamily: 'JHFontNormal,arial,sans-serif',
            textTransform: 'none',
          }}
          color="primary"
          size="small"
          text={t('ApplyFilter')}
          handleOnClick={() => {
            handleOnFilterPress(currentFilterContext.upToRows, 0);
          }}
          disabled={beginDateStartError || beginDateEndError}
          endIcon={<SearchIcon />}
        />
        <CustomButton
          sx={{
            fontFamily: 'JHFontNormal,arial,sans-serif',
            textTransform: 'none',
          }}
          color="secondary"
          size="small"
          endIcon={<FilterAltOffIcon />}
          text={t('ResetFilters')}
          handleOnClick={() => {
            handleOnFilterResetPress();
          }}
          disabled={
            JSON.stringify(currentFilterContext.divisions) ===
              JSON.stringify(divisionInitialSelection) &&
            JSON.stringify(currentFilterContext.countries) ===
              JSON.stringify(countriesInitialSelection) &&
            JSON.stringify(currentFilterContext.shipToParties) ===
              JSON.stringify(shipToPartiesInitialSelection) &&
            currentFilterContext.contractType === '' &&
            currentFilterContext.contractUsage === '' &&
            currentFilterContext.beginDateStart ===
              beginDateStartInitialSelection &&
            currentFilterContext.beginDateEnd ===
              beginDateEndInitialSelection &&
            currentFilterContext.serialNumber === '' &&
            currentFilterContext.contractNumber === '' &&
            currentFilterContext.customerReference === '' &&
            currentFilterContext.activeContracts === true &&
            currentFilterContext.finishedContracts === false
          }
        />
      </Stack>
    </Stack>
  );
};
